import { NodeExtension, ApplySchemaAttributes, NodeSpecOverride } from '@remirror/core';
import { extractInstagramReelId, isValidInstagramReelUrl } from '../../../../../helpers/instagram-reels';

/**
 * Rich Instagram Reels Extension for Remirror
 * This extension allows users to embed Instagram Reels in the rich text editor.
 */

export class RichInstagramReelsExtension extends NodeExtension {
  static extensionName = 'instagramReels';
  static disableExtraAttributes = true;

  get name() {
    return 'instagramReels' as const;
  }

  override createNodeSpec(extra: ApplySchemaAttributes, override: NodeSpecOverride) {
    return {
      attrs: {
        url: { default: '' },
        reelId: { default: '' },
        width: { default: 300 },
        height: { default: 533 }
      },
      group: 'block',
      draggable: false,
      atom: true,
      parseDOM: [
        {
          tag: 'div[data-instagram-reel]',
          getAttrs: (dom: any) => {
            const url = dom.getAttribute('data-url') || '';
            const reelId = dom.getAttribute('data-reel-id') || '';
            const width = parseInt(dom.getAttribute('data-width') || '300');
            const height = parseInt(dom.getAttribute('data-height') || '533');

            return { url, reelId, width, height };
          }
        }
      ],
      toDOM: (node) => {
        const { url, reelId, width, height } = node.attrs;

        return [
          'div',
          {
            'data-instagram-reel': 'true',
            'data-url': url,
            'data-reel-id': reelId,
            'data-width': width.toString(),
            'data-height': height.toString(),
            style: `
              margin: 16px 0;
              text-align: left;
              position: relative;
              width: ${width}px;
              height: ${height}px;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              overflow: hidden;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            `,
            ...extra.dom(node),
          }
        ] as any;
      }
    };
  }

  createCommands() {
    return {
      insertInstagramReel: (url: string) => ({ tr, dispatch }: any) => {
        if (!isValidInstagramReelUrl(url)) {
          return false;
        }

        const reelId = extractInstagramReelId(url);
        if (!reelId) {
          return false;
        }

        const node = this.type.create({
          url,
          reelId,
          width: 300,
          height: 533
        });

        if (dispatch) {
          tr.replaceSelectionWith(node);
          dispatch(tr);
        }

        return true;
      }
    };
  }

  createNodeViews() {
    return (node: any, view: any, getPos: any) => {
      const { url, reelId, width = 300, height = 533 } = node.attrs;

      // Crear el contenedor principal
      const container = document.createElement('div');
      container.className = 'instagram-reel-container';
      container.style.cssText = `
        margin: 16px 0;
        text-align: left;
        position: relative;
        width: ${width}px;
        height: ${height}px;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      `;

      // Función para actualizar el nodo
      const updateNode = (attrs: any) => {
        const pos = getPos();
        if (pos !== undefined) {
          view.dispatch(
            view.state.tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              ...attrs
            })
          );
        }
      };

      // Renderizar el contenido
      const renderContent = () => {
        container.innerHTML = '';

        if (!reelId) {
          container.innerHTML = `
            <div style="padding: 20px; text-align: center; background: #f8d7da; color: #721c24; border-radius: 8px; border: 1px solid #f5c6cb; height: 100%; display: flex; flex-direction: column; justify-content: center; align-items: center;">
              <div style="font-weight: 600; margin-bottom: 8px;">Error: No se pudo cargar el Instagram Reel</div>
              <button onclick="this.parentElement.parentElement.remove()" style="margin-top: 8px; padding: 4px 8px; background: #dc3545; color: white; border: none; border-radius: 4px; cursor: pointer;">Eliminar</button>
            </div>
          `;
          return;
        }

        // Crear el iframe de Instagram
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.instagram.com/reel/${reelId}/embed/`;
        iframe.width = '100%';
        iframe.height = '100%';
        iframe.frameBorder = '0';
        iframe.scrolling = 'no';
        iframe.style.cssText = `
          border: none;
          border-radius: 8px;
          display: block;
        `;

        container.appendChild(iframe);
      };

      // Renderizar el contenido inicial
      renderContent();

      return {
        dom: container,
        update: (newNode: any) => {
          if (newNode.type !== node.type) return false;
          if (JSON.stringify(newNode.attrs) !== JSON.stringify(node.attrs)) {
            node = newNode;
            renderContent();
          }
          return true;
        }
      };
    };
  }
}

export default RichInstagramReelsExtension;